<template>
  <RevDrawer
    :back-button-label="i18n(modalBuybackTranslations.backButtonLabel)"
    :close-button-label="i18n(modalBuybackTranslations.close)"
    :has-back-button="showBackButton"
    :name
    :title="activeStepTitle"
    @back="goToPrevStep"
    @close="handleClose"
  >
    <template #body>
      <div class="pt-12">
        <Intro
          v-if="activeStep === 'intro'"
          :has-identity
          :has-identity-informations
          :hasBankDetails
          :hasBirthdate
          :paymentTypes="paymentInfosData?.map((section) => section.type)"
          @next="(id) => goToNextStep(id)"
        />
        <BirthdateDetails
          v-if="activeStep === PAYMENT_INFOS_TYPES.DATE_OF_BIRTH"
          :id="props.id"
          :form-id="FORM_IDS.BIRTHDATE"
          :hasBirthdate
          :show-form="showBirthdateForm"
          @submit="handleBirthdateSubmit"
        />
        <BankDetails
          v-if="activeStep === PAYMENT_INFOS_TYPES.BANK_DETAILS"
          :bank="banks"
          :bank-data="bankDetails"
          :form-id="FORM_IDS.BANK_DETAILS"
          :isPending="isBankDetailsLoading"
          :show-form="showBankForm"
          :step="bankInformationStep"
          @submit="handleBankSubmit"
        />
        <Identity
          v-if="activeStep === PAYMENT_INFOS_TYPES.IDENTITY_DOCUMENT"
          :form-id="FORM_IDS.IDENTITY"
          :identity
          :show-form="showIdentityForm"
          :step="identityStep"
          @submit="handleIdentitySubmit"
        />
        <IdentityInformations
          v-if="
            activeStep === PAYMENT_INFOS_TYPES.PERSONAL_IDENTIFICATION ||
            activeStep === PAYMENT_INFOS_TYPES.TAX_IDENTIFICATION_NUMBER
          "
          :form-id="FORM_IDS.IDENTITY_INFORMATION"
          :identity-infos
          :show-form="showIdentityInformationsForm"
          :step="identityInformationsStep"
          @last-step="handleLastStepIdentityInformations"
          @submit="handleIdentityInformations"
        />
      </div>
    </template>
    <template #footer>
      <RevButton
        full-width="always"
        :loading="isLoading"
        variant="primary"
        @click="handleFooterBtn"
      >
        {{ footerBtnLabel }}
      </RevButton>
    </template>
  </RevDrawer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import type { HttpApiError } from '@backmarket/http-api'
import {
  type BankAccountPurposeType,
  type BankDetailsResponse,
  PAYMENT_INFOS_OPTIONS,
  PAYMENT_INFOS_TYPES,
  type PaymentInfoResponse,
  type PaymentInfosTypesType,
} from '@backmarket/http-api/src/api-specs-buyback/customer/client'
import BankDetails from '@backmarket/nuxt-layer-buyback/components/BankDetails/BankDetails.vue'
import type { FormValues } from '@backmarket/nuxt-layer-buyback/components/FormGenerator/FormGenerator.types'
import {
  type UnformattedIdentityPayload,
  identityPayloadAdapter,
} from '@backmarket/nuxt-layer-buyback/components/Identity/Identity.utils'
import Identity from '@backmarket/nuxt-layer-buyback/components/Identity/Identity.vue'
import type { IdDocumentsType } from '@backmarket/nuxt-layer-buyback/composables/config/useBuybackConfig'
import modalBuybackTranslations from '@backmarket/nuxt-layer-buyback/utils/Modal.translations'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'
import { RevButton } from '@ds/components/Button'
import { RevDrawer } from '@ds/components/Drawer'
import { submitForm } from '@ds/components/Form'
import { closeModal } from '@ds/components/ModalBase'

import type { BirthdateResponse } from '~/api/buyback-orders/types'

import {
  useBankDetails,
  useKycInformations,
} from '../../../composables/useKycInformations'

import translations from './KycDetailsModal.translations'
import BirthdateDetails from './birthdate/BirthdateDetails.vue'
import IdentityInformations from './identityInformations/IdentityInformations.vue'
import Intro from './intro/Intro.vue'

const MODAL_TITLE = {
  intro: translations.introTitle,
  [PAYMENT_INFOS_TYPES.BANK_DETAILS]: translations.bankDetailsTitle,
  [PAYMENT_INFOS_TYPES.IDENTITY_DOCUMENT]: translations.identityTitle,
  [PAYMENT_INFOS_TYPES.PERSONAL_IDENTIFICATION]:
    translations.identityInformationsTitle,
  [PAYMENT_INFOS_TYPES.TAX_IDENTIFICATION_NUMBER]:
    translations.identityInformationsTitle,
  [PAYMENT_INFOS_TYPES.DATE_OF_BIRTH]: translations.birthdateTitle,
}

const FORM_IDS = {
  BANK_DETAILS: 'banking-form',
  IDENTITY: 'identity-form',
  IDENTITY_INFORMATION: 'identity-information',
  BIRTHDATE: 'birthdate-form',
}

const props = defineProps<{
  name: string
  id: string
}>()

const identityStep = ref<'choice' | 'details'>('choice')
const identityInformationsStep = ref<'choice' | 'details'>('choice')
const bankInformationStep = ref<'choice' | 'details'>('details')
const activeStep = ref<PaymentInfosTypesType | 'intro'>('intro')
const i18n = useI18n()
const logger = useLogger()
const emit = defineEmits(['is-loaded', 'close'])
const { openErrorToast, openSuccessToast } = useTheToast()
const isLoading = ref(false)
const editMode = ref(false)
const paymentInfosData = ref<PaymentInfoResponse | null>()
const bankDetails = ref<BankDetailsResponse | null>(null)
const isBankDetailsLoading = ref(false)

const $httpFetchV2 = useHttpFetch()

function fetchData() {
  useKycInformations(props.id).then(
    ({ error: paymentInfoError, data: paymentInfoData }) => {
      if (paymentInfoError.value) {
        openErrorToast({
          title: i18n(translations.toastErrorTitle),
          content: i18n(translations.toastErrorContent),
        })

        logger.error(
          '[BUYBACK][API][MYSALES] Error get customer payment info',
          {
            error: paymentInfoError.value,
            owners: ['bot-squad-circularity-order-lifecycle-front'],
          },
        )
      } else {
        paymentInfosData.value = paymentInfoData.value
      }
      emit('is-loaded')
    },
  )
}

fetchData()

const birthdate = computed(() =>
  paymentInfosData.value?.find(
    ({ type }) => type === PAYMENT_INFOS_TYPES.DATE_OF_BIRTH,
  ),
)
const identity = computed(() =>
  paymentInfosData.value?.find(
    ({ type }) => type === PAYMENT_INFOS_TYPES.IDENTITY_DOCUMENT,
  ),
)
const identityInfos = computed(() =>
  paymentInfosData.value?.find(
    ({ type }) =>
      type === PAYMENT_INFOS_TYPES.PERSONAL_IDENTIFICATION ||
      type === PAYMENT_INFOS_TYPES.TAX_IDENTIFICATION_NUMBER,
  ),
)
const banks = computed(() =>
  paymentInfosData.value?.find(
    ({ type }) => type === PAYMENT_INFOS_TYPES.BANK_DETAILS,
  ),
)

const hasAccountPurposeSelection = computed(() => {
  return (
    banks.value?.options.some(
      (option) => option === PAYMENT_INFOS_OPTIONS.ACH,
    ) ?? false
  )
})

const hasBirthdate = computed(() => Boolean(birthdate.value?.filled))
const hasIdentity = computed(() => Boolean(identity.value?.filled))
const hasIdentityInformations = computed(() =>
  Boolean(identityInfos.value?.filled),
)
const hasBankDetails = computed(() => Boolean(banks.value?.filled))
const showBackButton = computed(() => activeStep.value !== 'intro')
const showBirthdateForm = computed(
  () =>
    activeStep.value === PAYMENT_INFOS_TYPES.DATE_OF_BIRTH &&
    (editMode.value || !hasBirthdate.value),
)
const showIdentityForm = computed(
  () =>
    activeStep.value === PAYMENT_INFOS_TYPES.IDENTITY_DOCUMENT &&
    (editMode.value || !hasIdentity.value),
)
const showBankForm = computed(
  () =>
    activeStep.value === PAYMENT_INFOS_TYPES.BANK_DETAILS &&
    (editMode.value || !hasBankDetails.value),
)
const showIdentityInformationsForm = computed(
  () =>
    (activeStep.value === PAYMENT_INFOS_TYPES.PERSONAL_IDENTIFICATION ||
      activeStep.value === PAYMENT_INFOS_TYPES.TAX_IDENTIFICATION_NUMBER) &&
    (editMode.value || !hasIdentityInformations.value),
)
const activeStepTitle = computed(() => {
  const title = MODAL_TITLE[activeStep.value]
  if (title) {
    return i18n(MODAL_TITLE[activeStep.value])
  }

  return ''
})

const closeBtn = computed(() => activeStep.value === 'intro')
const nextBtn = computed(
  () =>
    (showIdentityForm.value && identityStep.value === 'choice') ||
    (showIdentityInformationsForm.value &&
      identityInformationsStep.value === 'choice') ||
    (showBankForm.value && bankInformationStep.value === 'choice'),
)
const saveBtn = computed(
  () =>
    showBirthdateForm.value ||
    showIdentityForm.value ||
    showBankForm.value ||
    showIdentityInformationsForm.value,
)
const footerBtnLabel = computed(() => {
  if (closeBtn.value) {
    return i18n(translations.close)
  }
  if (nextBtn.value) {
    return i18n(translations.next)
  }

  if (saveBtn.value) {
    return i18n(translations.save)
  }

  return i18n(translations.update)
})

function handleClose() {
  emit('close')
}

function goToIntroStep() {
  identityStep.value = 'choice'
  identityInformationsStep.value = 'choice'
  bankInformationStep.value = hasAccountPurposeSelection.value
    ? 'choice'
    : 'details'
  editMode.value = false
  activeStep.value = 'intro'
}

function goToPrevStep() {
  switch (activeStep.value) {
    case PAYMENT_INFOS_TYPES.IDENTITY_DOCUMENT:
      if (identityStep.value === 'details') {
        identityStep.value = 'choice'

        return
      }
      break

    case PAYMENT_INFOS_TYPES.PERSONAL_IDENTIFICATION:
    case PAYMENT_INFOS_TYPES.TAX_IDENTIFICATION_NUMBER:
      if (
        identityInformationsStep.value === 'details' &&
        identityInfos.value?.options.length !== 1
      ) {
        identityInformationsStep.value = 'choice'

        return
      }
      break

    case PAYMENT_INFOS_TYPES.BANK_DETAILS:
      if (
        bankInformationStep.value === 'details' &&
        hasAccountPurposeSelection.value
      ) {
        bankInformationStep.value = 'choice'

        return
      }
      break

    default:
      goToIntroStep()
  }

  goToIntroStep()
}

async function goToNextStep(id: PaymentInfosTypesType) {
  activeStep.value = id

  if (id === PAYMENT_INFOS_TYPES.BANK_DETAILS) {
    isBankDetailsLoading.value = true
    const { data } = await useBankDetails()
    if (data.value) {
      bankDetails.value = data.value
    }
    if (hasAccountPurposeSelection.value) {
      bankInformationStep.value = 'choice'
    }
    isBankDetailsLoading.value = false
  }
}

function handleFooterBtn() {
  if (closeBtn.value) {
    closeModal()
  } else if (nextBtn.value) {
    identityStep.value = 'details'
    identityInformationsStep.value = 'details'
    bankInformationStep.value = 'details'
  } else if (saveBtn.value) {
    switch (activeStep.value) {
      case PAYMENT_INFOS_TYPES.DATE_OF_BIRTH:
        submitForm(FORM_IDS.BIRTHDATE)
        break
      case PAYMENT_INFOS_TYPES.BANK_DETAILS:
        submitForm(FORM_IDS.BANK_DETAILS)
        break
      case PAYMENT_INFOS_TYPES.IDENTITY_DOCUMENT:
        submitForm(FORM_IDS.IDENTITY)
        break
      case PAYMENT_INFOS_TYPES.PERSONAL_IDENTIFICATION:
      case PAYMENT_INFOS_TYPES.TAX_IDENTIFICATION_NUMBER:
        submitForm(FORM_IDS.IDENTITY_INFORMATION)
        break

      default:
    }
  } else {
    editMode.value = true
  }
}

async function handleBirthdateSubmit(values: BirthdateResponse) {
  isLoading.value = true
  try {
    await $httpFetchV2('/buyback/v1/customer/orders/:id/birthdate', {
      method: 'PUT',
      pathParams: {
        id: props.id,
      },
      body: {
        birthdate: values.birthdate,
      },
    })

    openSuccessToast({
      title: i18n(translations.toastBankStepTitle),
      content: i18n(translations.toastBankStepContent),
    })
    fetchData()
    goToIntroStep()
  } catch (err) {
    openErrorToast({
      title: i18n(translations.toastErrorTitle),
      content: i18n(translations.toastErrorContent),
    })

    logger.error('[BUYBACK][API][MYSALES] Error updating customer birthdate', {
      error: err as HttpApiError,
      owners: ['bot-squad-circularity-order-lifecycle-front'],
    })
  } finally {
    isLoading.value = false
  }
}

async function handleBankSubmit(
  values: FormValues,
  bankType: BankAccountPurposeType,
) {
  try {
    isLoading.value = true
    const body = hasAccountPurposeSelection.value
      ? removeEmptyValuesInObject({ ...values, bankAccountPurpose: bankType })
      : removeEmptyValuesInObject(values)

    await $httpFetchV2('/bm/client/bank_details', {
      method: 'PUT',
      body,
    })

    openSuccessToast({
      title: i18n(translations.toastBankStepTitle),
      content: i18n(translations.toastBankStepContent),
    })
    fetchData()
    goToIntroStep()
  } catch (err) {
    openErrorToast({
      title: i18n(translations.toastErrorTitle),
      content: i18n(translations.toastErrorContent),
    })

    logger.error(
      '[BUYBACK][API][MYSALES] Error updating customer bank details',
      {
        error: err as HttpApiError,
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      },
    )
  } finally {
    isLoading.value = false
  }
}

async function handleIdentitySubmit(
  values: UnformattedIdentityPayload,
  type: IdDocumentsType,
) {
  isLoading.value = true
  const apiPayload = identityPayloadAdapter(values, type)
  const body = new FormData()

  for (const [key, value] of Object.entries(apiPayload)) {
    if (value) {
      body.append(key, value as File)
    }
  }

  try {
    await $httpFetchV2('/bm/client/identity_documents', {
      method: 'POST',
      body,
    })

    openSuccessToast({
      title: i18n(translations.toastBankStepTitle),
      content: i18n(translations.toastBankStepContent),
    })

    fetchData()
    goToIntroStep()
  } catch (err) {
    openErrorToast({
      title: i18n(translations.toastErrorTitle),
      content: i18n(translations.toastErrorContent),
    })

    logger.error(
      '[BUYBACK][API][MYSALES] Error updating customer identity documents',
      {
        error: err as HttpApiError,
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      },
    )
  } finally {
    isLoading.value = false
  }
}

async function handleIdentityInformations(idNumber: string, idType: string) {
  isLoading.value = true
  try {
    await $httpFetchV2('/payout/customer/identity-number', {
      method: 'POST',
      body: {
        idType,
        idNumber,
      },
    })
    openSuccessToast({
      title: i18n(translations.toastBankStepTitle),
      content: i18n(translations.toastBankStepContent),
    })

    fetchData()
    goToIntroStep()
  } catch (err) {
    openErrorToast({
      title: i18n(translations.toastErrorTitle),
      content: i18n(translations.toastErrorContent),
    })

    logger.error(
      '[BUYBACK][API][MYSALES] Error updating customer identity number',
      {
        error: err as HttpApiError,
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      },
    )
  } finally {
    isLoading.value = false
  }
}

function handleLastStepIdentityInformations() {
  identityInformationsStep.value = 'details'
}
</script>
